"use client";

import {
  type HrtDescriptionEntry,
  getTranslatedHrtDisplayMetadata,
} from "@/app/directories/shop/helper";
import { parseInlineLinks } from "@/app/directories/shop/lib/parse-inline-links";
import { getSafeExternalUrl } from "@/app/directories/shop/lib/safe-external-url";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useLocale } from "next-intl";
import { Fragment, type ReactNode } from "react";
import { useProduct } from "./ProductContext";

const ExternalLink = ({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="underline underline-offset-2"
  >
    {children}
  </a>
);

/**
 * A safe `entry.link` makes the whole value the link text and takes precedence
 * over any inline `[text](url)` links in the value, which then render as-is.
 */
const DescriptionEntryContent = ({ entry }: { entry: HrtDescriptionEntry }) => {
  const entryHref = getSafeExternalUrl(entry.link);
  if (entryHref) {
    return <ExternalLink href={entryHref}>{entry.value}</ExternalLink>;
  }
  return parseInlineLinks(entry.value).map((segment) =>
    segment.href ? (
      <ExternalLink key={segment.offset} href={segment.href}>
        {segment.text}
      </ExternalLink>
    ) : (
      <Fragment key={segment.offset}>{segment.text}</Fragment>
    ),
  );
};

export function ProductDetails() {
  const locale = useLocale();
  const languageCode = locale.replace("-", "_");
  const { selectedOption } = useProduct();
  const t = useTranslations("Shop");
  const translatedMetadata = selectedOption?.hrt
    ? getTranslatedHrtDisplayMetadata(selectedOption.hrt, languageCode)
    : null;

  const description = translatedMetadata?.description;
  const ingredients = translatedMetadata?.ingredients;

  if (!description && !ingredients) {
    return null;
  }

  return (
    <div className="flex flex-col justify-between h-full ">
      <div className="max-h-full lg:overflow-y-auto sm:overflow-y-visible mb-4">
        {description?.map((item) => (
          <p className="font-light leading-[22px]" key={item.value}>
            <DescriptionEntryContent entry={item} />
          </p>
        ))}
      </div>
      {ingredients && ingredients.length > 0 && (
        <>
          <div className=" text-gray-500 font-mono">{t("Includes")}</div>
          <ul className="list-disc ml-4">
            {ingredients.map((item) => (
              <li key={item.value} className="font-light leading-[22px]">
                {item.value}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
